import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { chapters } from "@/mocks/chapters";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";

export default function ArticlePage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const chapter = useMemo(() => chapters.find((ch) => ch.slug === slug), [slug]);
  const relatedChapters = useMemo(() => {
    if (!chapter) return [];
    return chapters
      .filter((ch) => ch.category === chapter.category && ch.id !== chapter.id)
      .slice(0, 4);
  }, [chapter]);

  const prevChapter = useMemo(() => {
    if (!chapter) return null;
    const idx = chapters.findIndex((ch) => ch.id === chapter.id);
    return idx > 0 ? chapters[idx - 1] : null;
  }, [chapter]);

  const nextChapter = useMemo(() => {
    if (!chapter) return null;
    const idx = chapters.findIndex((ch) => ch.id === chapter.id);
    return idx < chapters.length - 1 ? chapters[idx + 1] : null;
  }, [chapter]);

  if (!chapter) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 flex items-center justify-center bg-background-100 rounded-full mb-4">
            <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-500">未找到该章节</p>
          <a href="/" className="mt-4 text-sm text-primary-500 hover:text-primary-600">
            返回首页
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: chapter.title,
    description: chapter.summary,
    image: chapter.coverImage,
    datePublished: chapter.publishDate,
    author: {
      "@type": "Person",
      name: "岸本齐史",
    },
    publisher: {
      "@type": "Organization",
      name: "火影漫画",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://naruto-manga.com/article/${chapter.slug}`,
    },
  };

  const relatedSchemas = relatedChapters.map((rc, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    item: {
      "@type": "Article",
      url: `https://naruto-manga.com/article/${rc.slug}`,
    },
  }));

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />

      <article className="pt-20 px-4 md:px-6" itemScope itemType="https://schema.org/Article">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: t("breadcrumb.home"), href: "/" },
              { label: chapter.category, href: `/category/${chapter.categorySlug}` },
              { label: chapter.title },
            ]}
          />

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "ItemList",
                itemListElement: relatedSchemas,
              }),
            }}
          />

          {/* Article Header */}
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-primary-100/70 text-primary-600 text-xs font-medium rounded-full">
                {chapter.category}
              </span>
              <div className="flex flex-wrap items-center gap-2">
                {chapter.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <h1
              className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight"
              itemProp="headline"
            >
              {chapter.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                <time dateTime={chapter.publishDate} itemProp="datePublished">
                  {chapter.publishDate}
                </time>
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {chapter.views.toLocaleString()} {t("readCount")}
              </span>
            </div>
          </header>

          {/* Article Cover */}
          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={chapter.coverImage}
              alt={chapter.title}
              title={chapter.title}
              className="w-full h-64 md:h-96 object-cover"
              itemProp="image"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose-custom mb-10"
            itemProp="articleBody"
          >
            {chapter.content.split("\n\n").map((paragraph, idx) => (
              <p
                key={idx}
                className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4"
              >
                {paragraph.trim()}
              </p>
            ))}
          </div>

          {/* Prev / Next Navigation */}
          <div className="flex flex-col sm:flex-row items-stretch gap-4 mb-12 pb-8 border-b border-background-200">
            {prevChapter ? (
              <a
                href={`/article/${prevChapter.slug}`}
                className="flex-1 flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 transition-all group"
              >
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-left-line text-lg text-foreground-500 group-hover:text-primary-500 transition-colors"></i>
                </div>
                <div className="min-w-0">
                  <span className="text-xs text-foreground-400">{t("article.prevChapter")}</span>
                  <p className="text-sm font-medium text-foreground-900 truncate">
                    {prevChapter.title}
                  </p>
                </div>
              </a>
            ) : (
              <div className="flex-1"></div>
            )}
            {nextChapter ? (
              <a
                href={`/article/${nextChapter.slug}`}
                className="flex-1 flex items-center justify-end gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 transition-all group text-right"
              >
                <div className="min-w-0">
                  <span className="text-xs text-foreground-400">{t("article.nextChapter")}</span>
                  <p className="text-sm font-medium text-foreground-900 truncate">
                    {nextChapter.title}
                  </p>
                </div>
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-right-line text-lg text-foreground-500 group-hover:text-primary-500 transition-colors"></i>
                </div>
              </a>
            ) : (
              <div className="flex-1"></div>
            )}
          </div>

          {/* Related Chapters */}
          {relatedChapters.length > 0 && (
            <section className="mb-16">
              <h2 className="text-xl font-bold text-foreground-950 mb-6">
                {t("article.relatedChapters")}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {relatedChapters.map((rc) => (
                  <a
                    key={rc.id}
                    href={`/article/${rc.slug}`}
                    className="flex gap-4 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 transition-all group"
                  >
                    <div className="w-16 h-20 flex-shrink-0 rounded-md overflow-hidden">
                      <img
                        src={rc.coverImage}
                        alt={rc.title}
                        title={rc.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs text-accent-500 font-medium">
                        {rc.category}
                      </span>
                      <h3 className="text-sm font-semibold text-foreground-900 mt-1 line-clamp-2 leading-snug">
                        {rc.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-1 mt-1">
                        {rc.summary}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <Footer />
    </div>
  );
}